import React, { useEffect, useState, useCallback } from 'react';
// @ts-ignore - Digital HIG components
import DHSnackbar from "@digital-hig/mui/Snackbar";
// @ts-ignore - Digital HIG components
import DHSlide from "@digital-hig/mui/Slide";
// @ts-ignore - Digital HIG components
import DHSvgIcon from "@digital-hig/mui/SvgIcon";
// @ts-ignore - Digital HIG components
import DHTypography from "@digital-hig/mui/Typography";
// @ts-ignore - Digital HIG components
import DHBox from "@digital-hig/mui/Box";
// @ts-ignore - Digital HIG components
import DHPaper from "@digital-hig/mui/Paper";
import ErrorIcon from './ErrorIcon';
import CloseIcon from './CloseIcon';

interface ToastProps {
  open: boolean;
  onClose: () => void;
  type: 'success' | 'error' | 'info' | 'warning';
  message: string;
  header?: string;
  duration?: number; // Auto-close duration in milliseconds (0 = no auto-close)
  position?: {
    top?: string;
    right?: string;
    left?: string;
    bottom?: string;
  };
}

interface MessageInfo {
  message: string;
  header?: string;
  type: 'success' | 'error' | 'info' | 'warning';
  key: number;
}

export default function Toast({ 
  open, 
  onClose, 
  type, 
  message, 
  header,
  duration = 5000,
  position = { top: '20px', right: '20px' }
}: ToastProps) {
  const [messageInfo, setMessageInfo] = useState<MessageInfo | undefined>(undefined);

  const handleClose = useCallback((event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (open) {
      setMessageInfo((prev) => ({
        ...prev,
        message: message,
        header: header,
        type: type,
        key: new Date().getTime(),
      }));
    }
  }, [open, message, header, type]);

  const handleExited = () => {
    setMessageInfo(undefined);
  };

  const getToastStyles = () => {
    const baseStyles = {
      borderRadius: '8px',
      padding: '16px 20px',
      boxShadow: `
        0px 2px 0px 0px hsla(0, 0%, 0%, 0.6),
        0px 1px 0px 0px hsla(0, 0%, 0%, 0.3) inset,
        1px 0px 0px 0px hsla(0, 0%, 0%, 0.3) inset,
        -1px 0px 0px 0px hsla(0, 0%, 0%, 0.3) inset
      `,
      maxWidth: '500px',
      minWidth: '300px',
      width: 'auto',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      fontFamily: 'Artifakt Element, sans-serif'
    };

    switch (messageInfo?.type) {
      case 'error':
        return {
          ...baseStyles,
          backgroundColor: 'hsla(0, 0%, 100%, 1)',
          color: '#333333'
        };
      default:
        return {
          ...baseStyles,
          backgroundColor: 'hsla(0, 0%, 100%, 1)',
          color: '#333333'
        };
    }
  };

  const getIcon = () => {
    switch (messageInfo?.type) {
      case 'error':
        return <ErrorIcon width={24} height={24} />;
      default:
        return null;
    }
  };

  if (!messageInfo) return null;

  return (
    <DHSnackbar
      open={open}
      onClose={handleClose}
      TransitionComponent={DHSlide}
      TransitionProps={{
        direction: 'left' as 'left' | 'right' | 'up' | 'down',
        onExited: handleExited,
      } as any}
      key={messageInfo.key}
      autoHideDuration={duration > 0 ? duration : undefined}
      anchorOrigin={{
        vertical: position.top ? 'top' : position.bottom ? 'bottom' : 'top',
        horizontal: position.right ? 'right' : position.left ? 'left' : 'right',
      }}
      sx={{
        top: position.top,
        right: position.right,
        left: position.left,
        bottom: position.bottom,
        zIndex: 9999,
        '& .MuiSnackbarContent-root': {
          backgroundColor: 'transparent',
          padding: 0,
          minWidth: 'auto',
          boxShadow: 'none',
        },
        '& .MuiSnackbarContent-message': {
          padding: 0,
          width: '100%',
        },
      }}
      message={
        <DHPaper
          className="dhig-background-white dhig-border-radius-2 dhig-padding-5 dhig-box-shadow-3"
          style={getToastStyles()}
        >
          <DHBox 
            className="dhig-display-flex dhig-align-start dhig-justify-space-between"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              flex: 1
            }}
          >
            {/* Icon and Content */}
            <DHBox 
              className="dhig-display-flex dhig-align-start"
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                flex: 1
              }}
            >
              <DHSvgIcon 
                viewBox="0 0 24 24"
                style={{
                  width: '24px',
                  height: '24px',
                  color: 'inherit',
                  flexShrink: 0,
                  marginTop: '1px'
                }}
              >
                {getIcon()}
              </DHSvgIcon>
              <DHBox 
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px',
                  flex: 1
                }}
              >
                {messageInfo.header && (
                  <DHTypography 
                    component="span"
                    sx={{
                      fontFamily: 'Artifakt Element',
                      fontWeight: '700',
                      fontSize: '14px',
                      lineHeight: '125%',
                      letterSpacing: '0%',
                      color: '#333333',
                      margin: 0,
                      marginTop: '5px',
                      display: 'block',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'pre-wrap'
                    }}
                  >
                    {messageInfo.header}
                  </DHTypography>
                )}
                <DHTypography 
                  variant="body-copy-medium"
                  className="dhig-typography-body-copy-medium"
                  style={{
                    fontFamily: 'Artifakt Element, sans-serif',
                    fontWeight: '400',
                    fontSize: '14px',
                    lineHeight: '150%',
                    letterSpacing: '0%',
                    color: 'inherit',
                    margin: 0,
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {messageInfo.message}
                </DHTypography>
              </DHBox>
            </DHBox>
            
            {/* Close Button */}
            <DHBox 
              sx={{
                cursor: 'pointer',
                flexShrink: 0,
                marginTop: '-4px'
              }}
              onClick={(e: React.MouseEvent) => handleClose(e)}
            >
              <CloseIcon width={32} height={32} color="black" />
            </DHBox>
          </DHBox>
        </DHPaper>
      }
    />
  );
}

